<template>
  <!--    Customer Form-->
  <validation-observer
    ref="customerForm"
    v-slot="{invalid}"
  >
    <sikanii-modal
      :title="`${updateCustomerMode?'Update':'Create'} ${resourceLabel}`"
      :show="customerModalOpened"
      :show-overlay="deletingCustomer"
      size="md"
      @onClose="closeCustomerModal()"
    >
      <b-row class="align-items-start">
        <div class="col-7">
          <sikanii-select
            v-model="selectedCustomerId"
            :placeholder="`All ${resourceLabel}s`"
            :options="customers"
            label-name="full_name"
            value-name="id"
            name="customers"
            @input="populateCustomerForm"
          />
        </div>
        <div class="col-2">
          <sikanii-dropdown
            icon="TrashIcon"
            name="delete_customer"
          >
            <b-dropdown-item
              id="cd_customer"
              @click="deleteCustomer()"
            >
              Yes
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </sikanii-dropdown>
        </div>
        <div class="col-2">
          <sikanii-button
            name="customer_action"
            variant="flat-danger"
            icon="XIcon"
            label=""
            tool-tip-text="Clear Form"
            @click="resetCustomerForm()"
          />
        </div>
      </b-row>
      <error-display :error="error" />
      <app-collapse
        accordion
        type="margin"
      >
        <app-collapse-item
          id="personal-info"
          title="Personal Information"
        >
          <sikanii-input
            v-model="customer.first_name"
            name="first_name"
            placeholder="Enter first name"
            rules="required"
            label="First Name"
          />
          <sikanii-input
            v-model="customer.last_name"
            name="last_name"
            placeholder="Enter last name"
            rules="required"
            label="Last Name"
          />
          <sikanii-input
            v-if="!customer.branch_id"
            v-model="customer.reference"
            name="reference"
            placeholder="Enter Account Number"
            label="Account Number"
          />
          <sikanii-date-picker
            v-model="customer.dob"
            name="dob"
            placeholder="Select Date of Birth"
            label="Date of Birth"
            rules="required"
          />
          <sikanii-select
            v-model="customer.sex"
            label-name="label"
            value-name="id"
            name="sex"
            rules="required"
            label="Sex"
            placeholder="Select Sex"
            :options="sexes"
          />
          <div class="mt-2">
            <sikanii-select
              v-model="customer.occupation_id"
              name="occupation_id"
              value-name="id"
              label-name="name"
              placeholder="Select Occupation"
              label="Occupation"
              :options="occupations"
              rules="required"
            />
          </div>
        </app-collapse-item>
        <app-collapse-item
          id="address-info"
          title="Contact Information"
        >
          <sikanii-input
            v-model="customer.phone"
            name="phone"
            placeholder="Enter Phone"
            label="Phone"
            rules="required|phone"
          />
          <sikanii-input
            v-model="customer.secondary_phone"
            name="secondary_phone"
            placeholder="Enter Secondary Phone"
            label="Secondary Phone"
            rules="phone"
          />
          <sikanii-input
            v-model="customer.email"
            name="email"
            placeholder="Enter Email"
            label="Email"
          />
          <sikanii-input
            v-model="customer.location"
            name="location"
            placeholder="Enter Location, eg. Kasoa New Market"
            label="Location"
            rules="required"
          />
          <sikanii-input
            v-model="customer.address"
            name="address"
            placeholder="Enter Address, eg. BS-020-1230"
            label="Address"
            rules="required"
          />
          <div
            v-if="!purchaserMode"
            class="mt-2"
          >
            <sikanii-select
              v-model="customer.branch_id"
              name="branch_id"
              value-name="id"
              label-name="name"
              placeholder="Select Branch"
              label="Branch"
              :options="branches"
            />
          </div>
        </app-collapse-item>
        <app-collapse-item
          id="next-kin-info"
          title="Next of Kin Information"
        >
          <sikanii-input
            v-model="customer.next_of_kin_name"
            name="next_of_kin_name"
            placeholder="Enter next of kin name"
            label="Next of Kin Name"
          />
          <sikanii-input
            v-model="customer.next_of_kin_address"
            name="next_of_kin_address"
            placeholder="Enter next of kin address"
            label="Next of Kin Address"
          />
          <sikanii-input
            v-model="customer.next_of_kin_phone"
            name="next_of_kin_phone"
            placeholder="Enter next of kin phone"
            label="Next of Kin Phone"
            rules="phone"
          />
        </app-collapse-item>
        <app-collapse-item
          v-if="!purchaserMode || isSusuCustomer"
          id="accounts-info"
          title="Contribution Information"
        >
          <sikanii-select
            v-if="!updateCustomerMode"
            v-model="customer.contribution_settings"
            label-name="title"
            value-name="id"
            name="contribution_settings"
            :rules="commissionRules"
            label="Commission Settings"
            placeholder="Select Settings"
            :options="settings"
          />
          <sikanii-multi-select
            v-model="customer.subscribed_services"
            label="Subscribed Services"
            placeholder="Select Subscribed Services"
            :options="subscribedServices"
            label-name="label"
            value-name="id"
            rules="required"
          />
          <div v-if="!updateCustomerMode">
            <div
              v-if="isSubscribedToSusu"
              class="mt-2"
            >
              <sikanii-input
                v-model="customer.susu_amount"
                name="susu_amount"
                placeholder="Enter Amount"
                label="Susu Amount"
                suffix-icon="HelpCircleIcon"
                rules="required|gt:0"
                type="number"
                tooltip="This is the amount this customer will pay periodically"
              />
            </div>
            <div
              v-if="susuOrSavingsSelected"
              class="mt-2"
            >
              <b-form-checkbox v-model="customer.deposit_initial_amount">
                Deposit Initial Amount
              </b-form-checkbox>
              <div v-if="customer.deposit_initial_amount">
                <div
                  v-if="isSubscribedToSusu"
                  class="mt-2"
                >
                  <sikanii-input
                    v-model="customer.initial_amount_for_susu"
                    name="initial_deposit_for_susu"
                    placeholder="Enter Amount"
                    label="Initial Deposit for Susu"
                  />
                </div>
                <div
                  v-if="isSubscribedToSavings"
                  class="mt-2"
                >
                  <sikanii-input
                    v-model="customer.initial_amount_for_savings"
                    name="initial_deposit_for_savings"
                    placeholder="Enter Amount"
                    label="Initial Deposit for Savings"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="mb-2" />
        </app-collapse-item>
      </app-collapse>
      <div class="mt-2">
        <sikanii-file-button
          :label="`Upload ${resourceLabel} Photo`"
          :show-files="true"
          variant="outline-primary"
          file-icon="ImageIcon"
          accept=".jpeg,.jpg,.png"
          :hide-remove="updateCustomerMode"
          :preview-files="photoPreview"
          @onFileRemoved="handlePhotoRemoved"
          @onFileUpload="handlePhotoUpload"
        />
      </div>
      <div class="mt-2">
        <sikanii-file-button
          label="Upload ID Document"
          variant="outline-primary"
          file-icon="FileTextIcon"
          accept=".pdf,.jpeg,.jpg,.png"
          :hide-remove="updateCustomerMode"
          :rules="updateCustomerMode?'':'required'"
          :preview-files="idDocumentPreview"
          @onFileRemoved="handleIdDocumentRemoved"
          @onFileUpload="handleIdDocumentUpload"
        />
      </div>
      <div class="mt-2">
        <sikanii-select
          v-model="customer.agent_id"
          name="agent_id"
          value-name="id"
          label-name="full_name"
          placeholder="Select Agent"
          label="Agent"
          :options="agents"
          rules="required"
        />
      </div>
      <div class="mt-2">
        <sikanii-select
          v-model="customer.status"
          name="status"
          value-name="id"
          label-name="label"
          placeholder="Select Status"
          label="Status"
          :options="statuses"
          rules="required"
        />
      </div>
      <div
        v-if="!purchaserMode"
        class="mt-2"
      >
        <b-form-checkbox v-model="customer.receive_notification">
          Subscribe to Notifications
        </b-form-checkbox>
      </div>
      <div
        v-if="!purchaserMode"
        class="mt-2"
      >
        <b-form-checkbox v-model="customer.is_purchaser">
          Make Customer a Purchaser?
        </b-form-checkbox>
      </div>
      <div
        v-if="purchaserMode && showSusuCustomerOption"
        class="mt-2"
      >
        <b-form-checkbox v-model="isSusuCustomer">
          Make Purchaser a Susu, Savings or Loans Customer?
        </b-form-checkbox>
      </div>
      <div
        v-else
        class="text-center text-primary"
      >
        <div
          v-if="purchaserMode && updateCustomerMode"
          class="mt-2"
        >
          <strong>This purchaser is also a susu/savings/loans customer</strong>
        </div>
      </div>
      <template slot="modal-actions">
        <b-row class="float-right">
          <sikanii-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeCustomerModal()"
          />
          <sikanii-button
            name="save_customer"
            :label="updateCustomerMode?'Update':'Create'"
            :disabled="invalid"
            :loading="customerLoading"
            loading-text="Loading..."
            @click="updateCustomerMode?updateCustomer():createCustomer()"
          />
        </b-row>
      </template>
    </sikanii-modal>
  </validation-observer>
</template>

<script>
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {
  BRow, BDropdownItem, BDropdownDivider, BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import SikaniiModal from '@/layouts/components/SikaniiModal.vue'
import SikaniiInput from '@/layouts/components/SikaniiInput.vue'
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiSelect from '@/layouts/components/SikaniiSelect.vue'
import SikaniiDropdown from '@/layouts/components/SikaniiDropdown.vue'
import SikaniiFileButton from '@/layouts/components/SikaniiFileButton.vue'
import SikaniiDatePicker from '@/layouts/components/SikaniiDatePicker.vue'
import SikaniiMultiSelect from '@/layouts/components/SikaniiMultiSelect.vue'
import logData from '@/libs/log'
import ErrorDisplay from '@/layouts/components/ErrorDisplay.vue'
import sexMixin from '@/libs/mixins/sexMixin'

const initialCustomer = {
  id: null,
  first_name: null,
  last_name: null,
  reference: null,
  deposit_initial_amount: false,
  initial_amount_for_susu: '',
  initial_amount_for_savings: '',
  phone: null,
  secondary_phone: null,
  email: null,
  sex: null,
  dob: null,
  susu_amount: null,
  contribution_settings: null,
  subscribed_services: [],
  photo: null,
  address: null,
  location: null,
  id_document: null,
  receive_notification: false,
  is_purchaser: false,
  status: null,
  occupation_id: null,
  branch_id: null,
  next_of_kin_name: null,
  next_of_kin_phone: null,
  next_of_kin_address: null,
}
export default {
  name: 'CustomerSetup',
  components: {
    AppCollapse,
    AppCollapseItem,
    ErrorDisplay,
    SikaniiMultiSelect,
    SikaniiDropdown,
    SikaniiSelect,
    SikaniiButton,
    SikaniiInput,
    SikaniiModal,
    ValidationObserver,
    SikaniiFileButton,
    SikaniiDatePicker,
    BRow,
    BDropdownItem,
    BDropdownDivider,
    BFormCheckbox,
  },
  mixins: [showToast, sexMixin],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    purchaserMode: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      statuses: [
        {
          label: 'Active',
          id: 'active',
        },
        {
          label: 'Inactive',
          id: 'inactive',
        },
      ],
      subscribedServices: [
        {
          label: 'Susu',
          id: 1,
        },
        {
          label: 'Savings',
          id: 2,
        },
        {
          label: 'Loans',
          id: 3,
        },
      ],
      error: {},
      photoPreview: [],
      idDocumentPreview: [],
      isSusuCustomer: false,
      customerModalOpened: false,
      updateCustomerMode: false,
      customerLoading: false,
      deletingCustomer: false,
      selectedCustomerId: null,
      showSusuCustomerOption: false,
      customer: { ...initialCustomer },
    }
  },
  computed: {
    resourceLabel() {
      return this.purchaserMode ? 'Purchaser' : 'Customer'
    },
    commissionRules() {
      const hasSusuOrSavings = !!this.customer.subscribed_services.find(s => s.id === 1 || s.id === 2)
      return hasSusuOrSavings ? 'required' : ''
    },
    isSubscribedToSavings() {
      return !!this.customer.subscribed_services.find(s => s.id === 2)
    },
    isSubscribedToSusu() {
      return !!this.customer.subscribed_services.find(s => s.id === 1)
    },
    susuOrSavingsSelected() {
      return !!this.customer.subscribed_services.filter(s => s.id === 1 || s.id === 2).length
    },
    selectedCustomer() {
      return this.customers.find(ter => ter.id === this.selectedCustomerId)
    },
    settings() {
      return this.$store.getters['susuSavings/settings']
    },
    agents() {
      return this.$store.getters['userAccounts/agents']
    },
    branches() {
      return this.$store.getters['susuSavings/branches']
    },
    occupations() {
      return this.$store.getters['susuSavings/occupations']
    },
    customers() {
      return this.$store.getters['susuSavings/customers']
    },
  },
  watch: {
    modalOpened(opened) {
      this.customerModalOpened = opened
    },
    editId(id) {
      this.selectedCustomerId = id
    },
  },
  methods: {
    handlePhotoRemoved(i) {
      this.photoPreview.splice(i, 1)
      this.customer.photo = null
    },
    handlePhotoUpload(data) {
      this.customer.photo = data.file
      this.photoPreview = [data.file]
    },
    handleIdDocumentRemoved(i) {
      this.idDocumentPreview.splice(i, 1)
      this.customer.id_document = null
    },
    handleIdDocumentUpload(data) {
      this.customer.id_document = data.file
      this.idDocumentPreview = [data.file]
    },
    resetCustomerForm() {
      this.customer = { ...initialCustomer }
      this.isSusuCustomer = false
      this.selectedCustomerId = null
      this.updateCustomerMode = false
      this.idDocumentPreview = []
      this.photoPreview = []
      this.$refs.customerForm.reset()
    },
    populateCustomerForm(customer) {
      if (this.selectedCustomer) {
        this.updateCustomerMode = true
        this.customer = { ...this.selectedCustomer }
        this.customer.is_purchaser = !!this.customer.is_purchaser
        this.isSusuCustomer = !!this.customer.has_susu || !!this.customer.has_savings || !!this.customer.has_loans
        this.showSusuCustomerOption = !this.isSusuCustomer
        if (this.customer.photo) {
          this.photoPreview = [{ name: `${this.customer.full_name}_Uploaded Photo` }]
        }
        if (this.customer.id_document) {
          this.idDocumentPreview = [{ name: `${this.customer.full_name}_Uploaded Document` }]
        }
      } else {
        this.resetCustomerForm()
      }
    },
    closeCustomerModal() {
      this.customerModalOpened = false
      this.$emit('modalClosed')
    },
    createCustomer() {
      this.error = {}
      if (!this.customer.id_document) {
        this.showInfo('Upload ID Document')
        return
      }
      this.customerLoading = true
      const customerData = new FormData()
      for (const key in this.customer) {
        if (key === 'photo' || key === 'id_document') {
          const file = this.customer[key]
          if (file) {
            customerData.append(key, file)
          }
        } else if (key === 'subscribed_services') {
          customerData.append(key, JSON.stringify(this.customer[key]))
        } else if (this.customer[key] !== null) {
          customerData.append(key, this.customer[key])
        }
      }
      if (this.purchaserMode) {
        customerData.append('receive_notification', 'true')
      }
      customerData.append('is_purchaser', (this.purchaserMode || this.customer.is_purchaser) ? 'yes' : 'no')
      this.$http.post('susu-savings/customers', customerData)
        .then(res => {
          this.showSuccess('Created customer successfully')
          this.$store.commit('susuSavings/addCustomer', res.data)
          this.resetCustomerForm()
        })
        .catch(err => {
          logData(err)
          this.error = err
          if (err.response.status === 403) {
            this.showInfo('You have reached the limit for total active customers allocated to you.')
          } else {
            this.showError('Failed to create customer')
          }
        })
        .finally(() => {
          this.customerLoading = false
        })
    },
    updateCustomer() {
      this.error = {}
      if (!this.customer.id) {
        return
      }
      this.customerLoading = true
      const customerData = new FormData()
      for (const key in this.customer) {
        if (key === 'photo' || key === 'id_document') {
          const file = this.customer[key]
          if (file) {
            if (file instanceof File) {
              customerData.append(key, file)
            }
          } else {
            customerData.append(`remove_${key}`, 'true')
          }
        } else if (key === 'subscribed_services') {
          customerData.append('subscribed_services', JSON.stringify(this.customer[key]))
        } else if (this.customer[key] !== null) {
          customerData.append(key, this.customer[key])
        }
      }
      customerData.append('is_purchaser', (this.purchaserMode || this.customer.is_purchaser) ? 'yes' : 'no')
      this.$http.post(`susu-savings/customers/${this.customer.id}`, customerData)
        .then(res => {
          this.showSuccess('Updated customer successfully')
          this.$store.commit('susuSavings/updateCustomer', res.data)
        })
        .catch(err => {
          logData(err)
          this.error = err
          if (err.response.status === 403) {
            this.showInfo('You have reached the limit for total active customers allocated to you.')
          } else {
            this.showError('Failed to update customer')
          }
        })
        .finally(() => {
          this.customerLoading = false
        })
    },
    deleteCustomer() {
      if (!this.selectedCustomerId) {
        this.showInfo('Please select a customer')
        return
      }
      this.deletingCustomer = true
      this.$http.delete(`susu-savings/customers/${this.selectedCustomerId}`)
        .then(() => {
          this.showSuccess('Deleted Successfully')
          this.$store.commit('susuSavings/removeCustomer', this.selectedCustomerId)
          this.selectedCustomerId = null
          this.resetCustomerForm()
        })
        .catch(() => {
          this.showError('Failed to delete customer')
        })
        .finally(() => {
          this.deletingCustomer = false
        })
    },
  }
  ,
}
</script>
